
import { Helmet } from "react-helmet";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { MessageSquare } from "lucide-react";

const Sobre = () => {
  const teamMembers = [
    {
      name: "Edno Muniz",
      role: "Especialista em SEO desde 2012",
      description: "Com vasta experiência em otimização para motores de busca, Edno ajuda empresas de todos os portes a conquistarem visibilidade online e atraírem clientes através de estratégias de SEO local personalizadas e eficientes."
    }
  ];

  // Schema data for person
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Edno Muniz",
    "jobTitle": "Especialista em SEO",
    "worksFor": {
      "@type": "Organization",
      "name": "LocalUp"
    },
    "description": "Especialista em SEO desde 2012, com vasta experiência em otimização para motores de busca."
  };

  // Schema for the about page
  const aboutPageSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "Sobre a LocalUp",
    "description": "A LocalUp é uma agência especializada em SEO Local com foco em resultados para pequenas e médias empresas.",
    "publisher": {
      "@type": "Organization",
      "name": "LocalUp",
      "logo": {
        "@type": "ImageObject",
        "url": "https://localup.com.br/logo.png"
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Sobre a LocalUp | Especialistas em SEO Local</title>
        <meta name="description" content="A LocalUp é uma agência especializada em SEO Local com foco em resultados para pequenas e médias empresas. Conheça nossa história e nossos especialistas." />
        <meta name="keywords" content="sobre LocalUp, agência SEO local, especialistas SEO, equipe SEO" />
        <link rel="canonical" href="https://localup.com.br/sobre" />
        <script type="application/ld+json">
          {JSON.stringify(personSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(aboutPageSchema)}
        </script>
      </Helmet>
      
      <Navbar />
      
      {/* Hero da página sobre */}
      <section className="py-16 px-6 md:px-12 bg-gradient-to-br from-white to-seo-light">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-6 text-seo-dark">Sobre a LocalUp</h1>
          <p className="text-xl mb-8 text-gray-700">
            Somos especialistas em SEO Local, ajudando empresas a serem encontradas pelos clientes certos, no momento certo.
          </p>
        </div>
      </section>

      {/* Nossa história */}
      <section className="py-16 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-seo-dark">Nossa História</h2>
          </div>
          
          <div className="bg-white p-8 rounded-xl shadow-md border border-gray-100">
            <p className="text-gray-700 mb-4">
              A LocalUp nasceu da percepção de que muitos pequenos e médios negócios no Brasil estavam perdendo oportunidades por não serem encontrados nas buscas locais. Nossos fundadores, com experiência em marketing digital e SEO, decidiram criar uma solução específica para esse problema.
            </p>
            <p className="text-gray-700 mb-4">
              Desde 2018, temos ajudado centenas de empresas a aumentarem sua visibilidade online e, consequentemente, seu número de clientes e faturamento. Nossa abordagem combina técnicas avançadas de SEO Local com um profundo entendimento das necessidades de cada tipo de negócio.
            </p>
            <p className="text-gray-700">
              Acreditamos que toda empresa, independentemente do seu tamanho, merece ser encontrada online por seus potenciais clientes. Por isso, desenvolvemos estratégias acessíveis e eficientes, com resultados comprovados.
            </p>
          </div>
        </div>
      </section>

      {/* Nossa equipe */}
      <section className="py-16 px-6 md:px-12 bg-seo-light">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-seo-dark">Nossa Equipe</h2>
            <p className="text-lg text-gray-700">
              Conheça quem trabalha para aumentar a visibilidade online do seu negócio.
            </p>
          </div>
          
          <div className="flex justify-center">
            {teamMembers.map((member, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md text-center max-w-md">
                <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-seo-primary/10 flex items-center justify-center">
                  <span className="text-2xl text-seo-primary font-bold">{member.name.charAt(0)}</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">{member.name}</h3>
                <p className="text-seo-primary font-medium mb-3">{member.role}</p>
                <p className="text-gray-600">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Missão, Visão, Valores */}
      <section className="py-16 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-seo-dark">Missão, Visão e Valores</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
              <h3 className="text-xl font-semibold mb-4 text-seo-primary">Missão</h3>
              <p className="text-gray-700">
                Conectar pequenas e médias empresas aos clientes certos através de estratégias de SEO Local eficientes e acessíveis.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
              <h3 className="text-xl font-semibold mb-4 text-seo-primary">Visão</h3>
              <p className="text-gray-700">
                Ser referência nacional em SEO Local, transformando a forma como as empresas brasileiras são encontradas online.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
              <h3 className="text-xl font-semibold mb-4 text-seo-primary">Valores</h3>
              <ul className="text-gray-700 list-disc pl-5">
                <li className="mb-1">Transparência</li>
                <li className="mb-1">Resultados mensuráveis</li>
                <li className="mb-1">Inovação constante</li>
                <li>Foco no cliente</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-gradient-to-r from-seo-primary to-seo-secondary text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">Vamos crescer juntos?</h2>
          <p className="text-xl mb-8 text-white/90">
            Entre em contato conosco e descubra como podemos ajudar seu negócio a ser encontrado por mais clientes.
          </p>
          <Button 
            onClick={() => window.open('https://wa.me/5511951974403', '_blank')}
            className="bg-white text-seo-primary hover:bg-white/90 text-lg px-8 py-6 rounded-lg shadow-lg flex items-center mx-auto gap-2"
          >
            <MessageSquare className="w-5 h-5" /> Fale com um especialista
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Sobre;
